import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BookOpen,
  Clock,
  Award,
  Play,
  Users,
  Star,
  ArrowRight,
  ShoppingCart,
  Heart,
  Search,
  Filter,
  Zap,
  TrendingUp
} from 'lucide-react'
import SEOHead from '../../components/seo/SEOHead'

interface Course {
  id: string
  title: string
  description: string
  instructor: string
  duration: string
  level: string
  price: number
  originalPrice?: number
  rating: number
  studentsCount: number
  image: string
  category: string
  tags: string[]
  isEnrolled?: boolean
  progress?: number
}

export default function UserCourses() {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [wishlist, setWishlist] = useState<string[]>([])
  const [cart, setCart] = useState<string[]>([])

  useEffect(() => {
    fetchCourses()
    loadWishlistAndCart()
  }, [])

  const loadWishlistAndCart = () => {
    try {
      const savedWishlist = JSON.parse(localStorage.getItem('userWishlist') || '[]')
      setWishlist(savedWishlist.map((item: any) => item.id))
      const savedCart = JSON.parse(localStorage.getItem('userCart') || '[]')
      setCart(savedCart.map((item: any) => item.id))
    } catch (error) {
      console.error('Error loading wishlist/cart:', error)
    }
  }

  const fetchCourses = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        navigate('/user/login')
        return
      }

      const [enrolledResponse, availableResponse] = await Promise.all([
        fetch('/api/enrollments', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/courses?published=true', { headers: { 'Authorization': `Bearer ${token}` } })
      ])

      let enrolledCourses: Course[] = []
      let availableCourses: Course[] = []

      if (enrolledResponse.ok) {
        const enrolledData = await enrolledResponse.json()
        enrolledCourses = (enrolledData.enrollments || [])
          .filter((e: any) => e.course)
          .map((enrollment: any) => ({
            id: enrollment.course._id,
            title: enrollment.course.title,
            description: enrollment.course.description,
            instructor: enrollment.course.instructor,
            duration: enrollment.course.duration,
            level: enrollment.course.level,
            price: enrollment.course.price,
            rating: enrollment.course.rating?.average,
            studentsCount: enrollment.course.enrollmentCount,
            image: enrollment.course.thumbnail || enrollment.course.image,
            category: enrollment.course.category?.name,
            tags: enrollment.course.tags,
            isEnrolled: true,
            progress: enrollment.progress || 0
          }))
      }

      if (availableResponse.ok) {
        const availableData = await availableResponse.json()
        const enrolledIds = enrolledCourses.map(c => c.id)
        availableCourses = (availableData.courses || [])
          .filter((course: any) => !enrolledIds.includes(course._id))
          .map((course: any) => ({
            id: course._id,
            title: course.title,
            description: course.description,
            instructor: course.instructor,
            duration: course.duration,
            level: course.level,
            price: course.price,
            originalPrice: course.originalPrice || course.discountPrice,
            rating: course.rating?.average || 4.5,
            studentsCount: course.enrollmentCount || 0,
            image: course.thumbnail || course.image || '',
            category: course.category?.name || 'General',
            tags: course.tags || [],
            isEnrolled: false
          }))
      }

      setCourses([...enrolledCourses, ...availableCourses])
    } catch (error) {
      console.error('Error fetching courses:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredCourses = courses.filter(course => {
    const matchesFilter = activeFilter === 'all' ||
      (activeFilter === 'enrolled' && course.isEnrolled) ||
      (activeFilter === 'available' && !course.isEnrolled)

    const matchesSearch = searchQuery === '' ||
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (course.category && course.category.toLowerCase().includes(searchQuery.toLowerCase()))

    return matchesFilter && matchesSearch
  })

  // Group courses for "Streaming App" layout
  const enrolledCourses = courses.filter(c => c.isEnrolled)
  const trendingCourses = courses.filter(c => !c.isEnrolled && c.rating >= 4.5)
  const newReleases = courses.filter(c => !c.isEnrolled).slice(0, 5)

  const handleCourseClick = (courseId: string, isEnrolled: boolean = false) => {
    navigate(isEnrolled ? `/user/courses/${courseId}/learn` : `/user/courses/${courseId}`)
  }

  const toggleWishlist = (e: React.MouseEvent, courseId: string) => {
    e.stopPropagation()
    const newWishlist = wishlist.includes(courseId)
      ? wishlist.filter(id => id !== courseId)
      : [...wishlist, courseId]

    setWishlist(newWishlist)

    // Update localStorage
    const allCourses = [...enrolledCourses, ...courses]
    const course = allCourses.find(c => c.id === courseId)

    if (course) {
      const savedWishlist = JSON.parse(localStorage.getItem('userWishlist') || '[]')
      if (wishlist.includes(courseId)) {
        // Remove
        const updated = savedWishlist.filter((item: any) => item.id !== courseId)
        localStorage.setItem('userWishlist', JSON.stringify(updated))
      } else {
        // Add
        savedWishlist.push({
          id: course.id,
          title: course.title,
          price: course.price,
          originalPrice: course.originalPrice,
          thumbnail: course.image,
          instructor: course.instructor
        })
        localStorage.setItem('userWishlist', JSON.stringify(savedWishlist))
      }
      // Dispatch event for header update if needed
      window.dispatchEvent(new Event('wishlist-updated'))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-4 border-violet-500/30 border-t-violet-600 rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-20">
      <SEOHead 
        title="Beauty & Makeup Courses"
        description="Explore professional beauty courses at Sudha's Academy. Learn bridal makeup, skincare, hair styling, nail art, and more. Industry-recognized certificates, expert instructors, flexible learning."
        keywords="beauty courses, makeup training, bridal makeup course, skincare certification, hair styling classes, nail art course, beauty diploma, professional makeup course India"
        url="https://sudhasacademy.com/user/courses"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Courses', url: '/user/courses' }
        ]}
      />
      {/* HERO SECTION - Cinematic Banner */}
      <div className="relative h-[500px] md:h-[400px] rounded-3xl overflow-hidden mb-12 group">
        <div className="absolute inset-0 bg-slate-900">
          <img
            src="https://images.unsplash.com/photo-1512496015851-a90fb38ba796?q=80&w=2070&auto=format&fit=crop"
            alt="Beauty Academy Hero"
            className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'https://images.unsplash.com/photo-1522337660859-02fbefca4702?q=80&w=2069&auto=format&fit=crop'; // Fallback image
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/40 to-transparent"></div>
        </div>

        <div className="absolute bottom-0 left-0 p-6 md:p-10 max-w-2xl w-full">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-3 py-1 bg-pink-600 text-white text-xs font-bold uppercase tracking-wider rounded-full">Featured</span>
            <span className="px-3 py-1 bg-white/10 backdrop-blur-md text-white text-xs font-bold uppercase tracking-wider rounded-full border border-white/20">Pro Certification</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight break-words hyphens-auto">Master the Art of <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">Professional Beauty</span></h1>
          <p className="text-base md:text-lg text-slate-300 mb-8 line-clamp-2">Elevate your career with our expert-led courses in makeup artistry, hair styling, and skincare. Join the next generation of beauty professionals.</p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <button
              onClick={() => {
                const enrolled = courses.filter(c => c.isEnrolled);
                if (enrolled.length > 0) {
                  navigate(`/user/courses/${enrolled[0].id}/learn`);
                } else {
                  document.getElementById('catalog')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="px-8 py-4 bg-white text-slate-900 font-bold rounded-xl hover:bg-slate-100 transition-colors flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5 fill-current" />
              Start Learning
            </button>
            <button
              onClick={() => document.getElementById('catalog')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white/10 backdrop-blur-md text-white font-bold rounded-xl hover:bg-white/20 transition-colors border border-white/20 text-center"
            >
              Browse Catalog
            </button>
          </div>
        </div>
      </div>

      {/* SEARCH & FILTERS - Floating Bar */}
      <div className="sticky top-4 z-30 mb-12">
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-slate-200 dark:border-slate-800 p-2 rounded-2xl shadow-xl flex flex-col md:flex-row items-center gap-4 max-w-4xl mx-auto">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Find your next skill..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-transparent text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl w-full md:w-auto overflow-x-auto scrollbar-hide">
            {['all', 'enrolled', 'available'].map(filter => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`flex-1 md:flex-none px-3 md:px-6 py-2.5 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap transition-all ${activeFilter === filter
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CONTENT SECTIONS */}
      <div className="space-y-16">

        {/* Enrolled Section (Horizontal Scroll) */}
        {enrolledCourses.length > 0 && (activeFilter === 'all' || activeFilter === 'enrolled') && (
          <section>
            <div className="flex items-center justify-between mb-6 px-4">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-500 fill-current" />
                Continue Watching
              </h2>
            </div>
            <div className="-mx-4 md:mx-0">
              <div className="flex overflow-x-auto pb-8 gap-6 px-4 md:px-0 custom-scrollbar snap-x">
                {enrolledCourses.map(course => (
                  <div
                    key={course.id}
                    onClick={() => handleCourseClick(course.id, true)}
                    className="flex-none w-[280px] md:w-[300px] snap-start group cursor-pointer"
                  >
                    <div className="relative aspect-video rounded-2xl overflow-hidden mb-4 shadow-lg">
                      <img src={course.image} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/40">
                          <Play className="w-5 h-5 text-white fill-current ml-1" />
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-700">
                        <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${course.progress}%` }}></div>
                      </div>
                    </div>
                    <h3 className="font-bold text-slate-900 dark:text-white truncate">{course.title}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{course.progress}% Complete</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Main Grid for All Courses */}
        {/* Main Grid for All Courses */}
        {(activeFilter === 'all' || activeFilter === 'available') && (
          <section className="px-4" id="catalog">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-violet-500" />
                Explore Courses
              </h2>
            </div>

            {filteredCourses.filter(c => !c.isEnrolled).length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                {filteredCourses.filter(c => !c.isEnrolled).map(course => (
                  <div
                    key={course.id}
                    onClick={() => handleCourseClick(course.id)}
                    className="group relative bg-white dark:bg-slate-800 rounded-3xl overflow-hidden border border-slate-100 dark:border-slate-700/50 hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-300 hover:-translate-y-2 cursor-pointer"
                  >
                    {/* Image Area */}
                    <div className="aspect-[4/3] relative overflow-hidden">
                      <img src={course.image} alt={course.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <div className="absolute top-4 right-4">
                        <button
                          onClick={(e) => toggleWishlist(e, course.id)}
                          className="w-10 h-10 rounded-full bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-pink-50 dark:hover:bg-pink-900/30 transition-colors group/btn z-10"
                        >
                          <Heart className={`w-5 h-5 transition-colors ${wishlist.includes(course.id) ? 'text-pink-500 fill-current' : 'text-slate-400 group-hover/btn:text-pink-500'}`} />
                        </button>
                      </div>
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm text-xs font-bold uppercase tracking-wider rounded-lg shadow-lg">
                          {course.category}
                        </span>
                      </div>
                    </div>

                    {/* Content Area */}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center text-yellow-500">
                          <Star className="w-4 h-4 fill-current" />
                          <span className="text-sm font-bold ml-1">{course.rating}</span>
                        </div>
                        <span className="text-slate-300">•</span>
                        <span className="text-xs font-medium text-slate-500">{course.studentsCount} Students</span>
                      </div>

                      <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-2 line-clamp-2 leading-tight group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                        {course.title}
                      </h3>

                      <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mb-6">
                        {course.description}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-700/50">
                        <div className="flex flex-col">
                          <span className="text-xs text-slate-400 uppercase font-bold">Price</span>
                          <div className="flex items-baseline gap-2">
                            <span className="text-xl font-black text-slate-900 dark:text-white">₹{course.price}</span>
                            {course.originalPrice && (
                              <span className="text-sm text-slate-400 line-through">₹{course.originalPrice}</span>
                            )}
                          </div>
                        </div>
                        <button className="w-12 h-12 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700">
                {searchQuery ? (
                  <div>
                    <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">No courses found</h3>
                    <p className="text-slate-500 dark:text-slate-400 mt-2">We couldn't find any courses matching "{searchQuery}"</p>
                  </div>
                ) : (
                  <div>
                    <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="w-8 h-8 text-yellow-600 dark:text-yellow-500" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">You're a Super Learner!</h3>
                    <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-md mx-auto">
                      You've enrolled in all our available courses. Check back soon for new content!
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  )
}